<template>
  <div class="history-modal__overlay" @click.self="$emit('close')">
    <div class="history-modal">
      <div class="history-modal__header">
        <h3>Histórico de edições</h3>
        <button class="history-modal__close" @click="$emit('close')">&times;</button>
      </div>

      <div v-if="loading" class="history-modal__loading">Carregando histórico...</div>
      <div v-else-if="historico.length === 0" class="history-modal__empty">Nenhuma edição anterior encontrada.</div>

      <div v-else class="history-modal__list">
        <div
          v-for="item in historico"
          :key="item.id"
          class="history-modal__item"
          :class="{ 'history-modal__item--selected': selectedId === item.id }"
          @click="selectedId = item.id"
        >
          <div class="history-modal__item-info">
            <span class="history-modal__item-date">{{ formatDate(item.created_at) }}</span>
            <span v-if="item.editado_por" class="history-modal__item-author">por {{ item.editado_por }}</span>
          </div>
          <div class="history-modal__item-excerpt">
            {{ stripHtml(item.conteudo).substring(0, 120) }}...
          </div>
        </div>
      </div>

      <div v-if="selectedId && selectedItem" class="history-modal__preview">
        <div class="history-modal__preview-label">Preview da versão selecionada</div>
        <div class="history-modal__preview-content" v-html="selectedItem.conteudo"></div>
      </div>

      <div v-if="historico.length > 0" class="history-modal__footer">
        <button
          class="history-modal__btn history-modal__btn--restore"
          :disabled="!selectedId || restoring"
          @click="restore"
        >
          {{ restoring ? 'Restaurando...' : 'Restaurar esta versão' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import api from '@/api';

export default {
  name: 'HistoryModal',
  props: {
    versaoId: { type: [Number, String], required: true },
  },
  emits: ['close', 'restored'],
  setup(props, { emit }) {
    const loading = ref(false);
    const restoring = ref(false);
    const historico = ref([]);
    const selectedId = ref(null);

    const selectedItem = computed(() =>
      historico.value.find(h => h.id === selectedId.value)
    );

    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    };

    const stripHtml = (html) => {
      const tmp = document.createElement('div');
      tmp.innerHTML = html || '';
      return tmp.textContent || '';
    };

    const loadHistorico = async () => {
      loading.value = true;
      try {
        const res = await api.getHistoricoVersao(props.versaoId);
        historico.value = res.data || [];
      } catch {
        historico.value = [];
      } finally {
        loading.value = false;
      }
    };

    const restore = async () => {
      if (!selectedId.value) return;
      restoring.value = true;
      try {
        await api.restaurarVersao(props.versaoId, selectedId.value);
        emit('restored', selectedItem.value.conteudo);
      } catch (err) {
        alert('Erro ao restaurar: ' + (err.response?.data?.message || err.message));
      } finally {
        restoring.value = false;
      }
    };

    onMounted(loadHistorico);

    return { loading, restoring, historico, selectedId, selectedItem, formatDate, stripHtml, restore };
  },
};
</script>

<style scoped>
.history-modal__overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.history-modal {
  background: #fff;
  border-radius: 8px;
  width: 700px;
  max-width: 95vw;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 30px rgba(0,0,0,0.2);
}

.history-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}

.history-modal__header h3 { margin: 0; font-size: 17px; }

.history-modal__close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  line-height: 1;
}

.history-modal__loading,
.history-modal__empty {
  padding: 40px 20px;
  text-align: center;
  color: #888;
  font-size: 14px;
}

.history-modal__list {
  overflow-y: auto;
  max-height: 220px;
  border-bottom: 1px solid #eee;
}

.history-modal__item {
  padding: 10px 20px;
  cursor: pointer;
  border-bottom: 1px solid #f5f5f5;
  transition: background 0.1s;
}

.history-modal__item:hover { background: #f8f9fa; }

.history-modal__item--selected {
  background: #e8f0fe;
  border-left: 3px solid #1a73e8;
}

.history-modal__item-info {
  display: flex;
  gap: 8px;
  font-size: 13px;
  margin-bottom: 4px;
}

.history-modal__item-date { font-weight: 600; color: #333; }
.history-modal__item-author { color: #888; }

.history-modal__item-excerpt {
  font-size: 12px;
  color: #666;
  line-height: 1.4;
}

.history-modal__preview {
  padding: 12px 20px;
  border-bottom: 1px solid #eee;
  max-height: 200px;
  overflow-y: auto;
}

.history-modal__preview-label {
  font-size: 11px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  margin-bottom: 6px;
}

.history-modal__preview-content {
  font-size: 14px;
  line-height: 1.5;
  color: #333;
}

.history-modal__footer {
  padding: 14px 20px;
  display: flex;
  justify-content: flex-end;
}

.history-modal__btn {
  padding: 8px 20px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.history-modal__btn--restore {
  background: #1a73e8;
  color: #fff;
}

.history-modal__btn--restore:hover { background: #1557b0; }
.history-modal__btn--restore:disabled { background: #93b4e8; cursor: not-allowed; }
</style>
